import {
  User,
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth } from "../lib/firebase";

type AdminStatus = "loading" | "signedOut" | "authorized" | "denied";

type AuthContextValue = {
  adminStatus: AdminStatus;
  user: User | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOutAdmin: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function hasAdminClaim(claims: Record<string, unknown>) {
  // Firebase custom claims are expected on the ID token as either:
  // admin === true, isChorAdmin === true, or role === "admin".
  return claims.admin === true || claims.isChorAdmin === true || claims.role === "admin";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [adminStatus, setAdminStatus] = useState<AdminStatus>("loading");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    void setPersistence(auth, browserLocalPersistence);

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!nextUser) {
        setUser(null);
        setAdminStatus("signedOut");
        return;
      }

      setUser(nextUser);
      setAdminStatus("loading");

      try {
        const tokenResult = await nextUser.getIdTokenResult(true);
        setAdminStatus(hasAdminClaim(tokenResult.claims) ? "authorized" : "denied");
      } catch {
        setAdminStatus("denied");
      }
    });

    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      adminStatus,
      user,
      isAdmin: adminStatus === "authorized",
      signIn: async (email, password) => {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        const tokenResult = await credential.user.getIdTokenResult(true);

        // Firebase custom claims must be assigned server-side before this check can pass.
        if (!hasAdminClaim(tokenResult.claims)) {
          setUser(credential.user);
          setAdminStatus("denied");
          return;
        }

        setUser(credential.user);
        setAdminStatus("authorized");
      },
      signOutAdmin: async () => {
        await signOut(auth);
      }
    }),
    [adminStatus, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
